import React, { useMemo, useRef, useState } from "react";
import { TextField, Button, CircularProgress, Chip } from "@mui/material";
import { Add, Check } from "@mui/icons-material";
import { useSidebarFormStore } from "../../../Zustand/sidebarForm.store";
import { useNotificationStore } from "../../../Zustand/notification.store";
import { useApiCreateMunicipalityRequest } from "../../../API/municipalities.api";
import { useAuthStore } from "../../../Zustand/auth.store";
import {
  EC2Municipality,
  States,
} from "../../../API/types/documentationCatalogs.types";

interface SidebarFormProps {
  isNewState: boolean;
  currentState: States | null;
  setCurrentMunicipality: (municipality: EC2Municipality | null) => void;
}

interface StagedMunicipality {
  id: string;
  name: string;
  websiteUrl: string;
}

const SidebarForm: React.FC<SidebarFormProps> = ({
  isNewState,
  currentState,
  setCurrentMunicipality,
}) => {
  /**
   * Custom hooks
   */
  const workdayUsername = useAuthStore((s) => s.workdayUsername);
  const customState = useSidebarFormStore((s) => s.customState);
  const setCustomState = useSidebarFormStore((s) => s.setCustomState);
  const customMunicipality = useSidebarFormStore((s) => s.customMunicipality);
  const setCustomMunicipality = useSidebarFormStore(
    (s) => s.setCustomMunicipality
  );
  const websiteUrl = useSidebarFormStore((s) => s.websiteUrl);
  const setWebsiteUrl = useSidebarFormStore((s) => s.setWebsiteUrl);
  const setShowCustomStateInput = useSidebarFormStore(
    (s) => s.setShowCustomStateInput
  );
  const setShowCustomMunicipalityInput = useSidebarFormStore(
    (s) => s.setShowCustomMunicipalityInput
  );
  const showNotification = useNotificationStore((s) => s.show);

  /**
   * States
   */
  const [stagedMunicipalities, setStagedMunicipalities] = useState<
    StagedMunicipality[]
  >([]);
  const [editingMunicipalityId, setEditingMunicipalityId] = useState<
    string | null
  >(null);
  const lastSubmitCountRef = useRef(0);

  /**
   * API
   */
  // 🟣 API - Create new municipality request
  const {
    call: callCreateMunicipalityRequest,
    loading: createMunicipalityRequestLoading,
  } = useApiCreateMunicipalityRequest({
    onCompleted: () => {
      // Restore initial states
      setShowCustomStateInput(false);
      setShowCustomMunicipalityInput(false);
      setCurrentMunicipality(null);
      setCustomMunicipality("");
      setWebsiteUrl("");
      setStagedMunicipalities([]);
      setEditingMunicipalityId(null);
      // Show success message
      const msg = isNewState
        ? `${customState} Request Submitted`
        : `${lastSubmitCountRef.current || 1} Municipality Request${
            lastSubmitCountRef.current === 1 ? "" : "s"
          } Submitted`;
      showNotification(msg, "success");
    },
    onError: () => {
      showNotification("Request failed, please try again", "error");
    },
  });

  /**
   * Helper functions
   */
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isCurrentInputInvalid = websiteUrl !== "" && !isValidUrl(websiteUrl);
  const canUseCurrentMunicipality =
    customMunicipality.trim() !== "" && !isCurrentInputInvalid;

  /**
   * Callbacks
   */
  // 🟤 Cb - handle submit create State/Municipality request
  const handleSubmit = () => {
    const baseRequest = {
      stateId: isNewState ? undefined : currentState?.stateId,
      stateName: isNewState ? customState : currentState?.name || "",
      action: "create" as const,
      userName: workdayUsername ?? "",
      isState: isNewState,
    };
    const requests = isNewState
      ? [
          {
            ...baseRequest,
            requestedMunicipalityName: customMunicipality || "",
            websiteUrl: websiteUrl || "",
          },
        ]
      : pendingMunicipalities.map((municipality) => ({
          ...baseRequest,
          requestedMunicipalityName: municipality.name,
          websiteUrl: municipality.websiteUrl || "",
        }));
    lastSubmitCountRef.current = requests.length;
    callCreateMunicipalityRequest(requests).catch((e) => console.error(e));
  };

  // 🟤 Cb - handle add municipality to chip list
  const addOrUpdateMunicipality = () => {
    if (!canUseCurrentMunicipality || isNewState) {
      return;
    }
    const trimmedName = customMunicipality.trim();
    setStagedMunicipalities((prev) => {
      if (editingMunicipalityId) {
        return prev.map((municipality) =>
          municipality.id === editingMunicipalityId
            ? {
                ...municipality,
                name: trimmedName,
                websiteUrl,
              }
            : municipality
        );
      }
      const newMunicipality: StagedMunicipality = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: trimmedName,
        websiteUrl,
      };
      return [...prev, newMunicipality];
    });
    setCustomMunicipality("");
    setWebsiteUrl("");
    setEditingMunicipalityId(null);
  };

  // 🟤 Cb - handle remove municipality from chip list
  const removeMunicipality = (id: string) => {
    setStagedMunicipalities((prev) =>
      prev.filter((municipality) => municipality.id !== id)
    );
    if (editingMunicipalityId === id) {
      setEditingMunicipalityId(null);
      setCustomMunicipality("");
      setWebsiteUrl("");
    }
  };

  // 🟤 Cb - handle format chip label
  const formatChipLabel = (municipality: StagedMunicipality) => {
    if (!municipality.websiteUrl) {
      return municipality.name;
    }
    const trimmedUrl =
      municipality.websiteUrl.length > 30
        ? `${municipality.websiteUrl.slice(0, 27)}...`
        : municipality.websiteUrl;
    return `${municipality.name} • ${trimmedUrl}`;
  };

  /**
   * Memos
   */
  // 🔵 Memo - Municipality chip list plus currently editable municipality
  const pendingMunicipalities = useMemo(() => {
    if (isNewState) {
      return [];
    }
    const items = [...stagedMunicipalities];
    if (canUseCurrentMunicipality) {
      const trimmedName = customMunicipality.trim();
      if (editingMunicipalityId) {
        const index = items.findIndex(
          (municipality) => municipality.id === editingMunicipalityId
        );
        if (index >= 0) {
          items[index] = {
            ...items[index],
            name: trimmedName,
            websiteUrl,
          };
        } else {
          items.push({
            id: editingMunicipalityId,
            name: trimmedName,
            websiteUrl,
          });
        }
      } else if (
        !items.some(
          (municipality) =>
            municipality.name === trimmedName &&
            municipality.websiteUrl === websiteUrl
        )
      ) {
        items.push({
          id: "current-input",
          name: trimmedName,
          websiteUrl,
        });
      }
    }
    return items;
  }, [
    canUseCurrentMunicipality,
    customMunicipality,
    editingMunicipalityId,
    isNewState,
    stagedMunicipalities,
    websiteUrl,
  ]);

  const isSubmitDisabled =
    createMunicipalityRequestLoading ||
    isCurrentInputInvalid ||
    (isNewState ? !customState : pendingMunicipalities.length === 0);

  return (
    <div className="flex flex-col items-end">
      {isNewState && (
        <TextField
          label="Enter State"
          fullWidth
          value={customState}
          onChange={(e) => setCustomState(e.target.value)}
          sx={{ mb: 2 }}
          required={isNewState}
        />
      )}
      <TextField
        label="Enter Municipality"
        fullWidth
        value={customMunicipality}
        onChange={(e) => setCustomMunicipality(e.target.value)}
        sx={{ mb: 2 }}
        required={!isNewState}
      />
      <TextField
        label="Website URL (Optional)"
        fullWidth
        value={websiteUrl}
        onChange={(e) => setWebsiteUrl(e.target.value)}
        error={websiteUrl !== "" && !isValidUrl(websiteUrl)}
        helperText={
          websiteUrl !== "" && !isValidUrl(websiteUrl)
            ? "Please enter a valid URL"
            : ""
        }
        sx={{ mb: 2 }}
      />
      <div className="flex w-full gap-2">
        {!isNewState && (
          <Button
            variant="outlined"
            sx={{ mb: 2 }}
            disabled={
              !canUseCurrentMunicipality || createMunicipalityRequestLoading
            }
            onClick={addOrUpdateMunicipality}
          >
            <Add fontSize="small" />
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ mb: 2 }}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {createMunicipalityRequestLoading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            <Check fontSize="small" />
          )}
        </Button>
      </div>
      {!isNewState && stagedMunicipalities.length > 0 && (
        <div className="flex w-full flex-wrap gap-2 mb-2">
          {stagedMunicipalities.map((municipality) => (
            <Chip
              key={municipality.id}
              label={formatChipLabel(municipality)}
              color={
                editingMunicipalityId === municipality.id
                  ? "primary"
                  : "default"
              }
              onClick={() => {
                setEditingMunicipalityId(municipality.id);
                setCustomMunicipality(municipality.name);
                setWebsiteUrl(municipality.websiteUrl);
              }}
              onDelete={() => removeMunicipality(municipality.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SidebarForm;
